// Auth Store (Zustand)

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AuthenticatedUser, MembershipRole } from '@calculom/shared-types';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  user: AuthenticatedUser | null;
  isAuthenticated: boolean;

  // Actions
  setTokens: (accessToken: string, refreshToken: string, expiresAt?: number) => void;
  setUser: (user: AuthenticatedUser) => void;
  login: (accessToken: string, refreshToken: string, user: AuthenticatedUser, expiresAt?: number) => void;
  logout: () => void;
  updateUser: (updates: Partial<AuthenticatedUser>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      user: null,
      isAuthenticated: false,

      setTokens: (accessToken, refreshToken, expiresAt) =>
        set({
          accessToken,
          refreshToken,
          ...(expiresAt !== undefined && { expiresAt }),
        }),

      setUser: (user) =>
        set({
          user,
          isAuthenticated: true,
        }),

      login: (accessToken, refreshToken, user, expiresAt) =>
        set({
          accessToken,
          refreshToken,
          expiresAt: expiresAt ?? null,
          user,
          isAuthenticated: true,
        }),

      logout: () =>
        set({
          accessToken: null,
          refreshToken: null,
          expiresAt: null,
          user: null,
          isAuthenticated: false,
        }),

      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),
    }),
    {
      name: 'calculom-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        expiresAt: state.expiresAt,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Hook for checking permissions
export function useHasPermission(permission: string): boolean {
  const { user } = useAuthStore();
  return user?.role === 'tenant_admin' || false; // Simplified - expand based on ROLE_PERMISSIONS
}

// Hook for checking role
export function useHasRole(...roles: MembershipRole[]): boolean {
  const { user } = useAuthStore();
  return user ? roles.includes(user.role) : false;
}
